import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../../lib/supabase'

type FeedbackType = 'suggestion' | 'bug' | 'compliment'
type NotifyType = 'success' | 'error' | 'warning' | 'info' | 'danger'

interface FeedbackSectionProps {
    userEmail: string
    showNotify: (title: string, message: string, type: NotifyType) => void
    isLoading: boolean
}

export default function FeedbackSection({ userEmail, showNotify, isLoading: globalLoading }: Readonly<FeedbackSectionProps>) {
    const { t } = useTranslation()
    const [feedbackType, setFeedbackType] = useState<FeedbackType>('suggestion')
    const [feedbackMessage, setFeedbackMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    // eslint-disable-next-line sonarjs/deprecation
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        try {
            setIsSubmitting(true)

            const { error } = await supabase.functions.invoke('send-feedback', {
                body: {
                    type: feedbackType,
                    message: feedbackMessage,
                    email: userEmail,
                },
            })

            if (error) throw error

            setFeedbackMessage('')
            setFeedbackType('suggestion')
            showNotify(t('settings.feedback.notifications.thanksTitle'), t('settings.feedback.notifications.received'), 'success')
        } catch (error) {
            console.error('Error sending feedback:', error)
            showNotify('Error', t('settings.feedback.notifications.error', { error: (error as Error).message }), 'danger')

            // Fallback: offer mailto if function fails
            const mailtoLink = `mailto:alpepasai@gmail.com?subject=Bibliotelia Feedback: ${feedbackType}&body=${encodeURIComponent(feedbackMessage)}`
            console.log('Sugerencia de contacto directo:', mailtoLink)
        } finally {
            setIsSubmitting(false)
        }
    }

    const loading = globalLoading || isSubmitting

    return (
        <section className="settings-section">
            <div className="section-header">
                <div className="section-icon">💬</div>
                <div>
                    <h3 className="section-title">{t('settings.feedback.title')}</h3>
                    <p className="section-desc">{t('settings.feedback.subtitle')}</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="feedback-form">
                <div className="form-group">
                    <label>{t('settings.feedback.typeLabel')}</label>
                    <select
                        value={feedbackType}
                        onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                        className="form-select"
                    >
                        <option value="suggestion">{t('settings.feedback.types.suggestion')}</option>
                        <option value="bug">{t('settings.feedback.types.bug')}</option>
                        <option value="compliment">{t('settings.feedback.types.compliment')}</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>{t('settings.feedback.messageLabel')}</label>
                    <textarea
                        value={feedbackMessage}
                        onChange={(e) => setFeedbackMessage(e.target.value)}
                        placeholder={t('settings.feedback.placeholder')}
                        required
                        className="form-textarea"
                    />
                </div>

                <div style={{ marginTop: '1rem' }}>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? t('settings.feedback.loading') : t('settings.feedback.submit')}
                    </button>
                </div>
            </form>
        </section>
    )
}
